// =============================================================================
// PORTAL UPLOAD PAGE (PortalUploadPage.js)
// =============================================================================
// File upload page for the CDiC Upload Portal tab.
// Adapted from cdic-portal-react/client/src/pages/UploadPage.js
//
// KEY CHANGES:
// - Import paths point to local portal files
// - Download links use /api/download/ (proxied to port 5000)
// - CSS class names prefixed with cdic-portal-
// =============================================================================

import React, { useState, useEffect } from 'react';
import { getUploadInfo, uploadFile } from './portalApi';
import PortalLayout from './PortalLayout';
import PortalFlashMessage from './PortalFlashMessage';

function PortalUploadPage({ onNavigate }) {
  const [name, setName] = useState('');
  const [welcomeText, setWelcomeText] = useState('');
  const [uploadInstructions, setUploadInstructions] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    async function fetchInfo() {
      try {
        const data = await getUploadInfo();
        setName(data.name);
        setWelcomeText(data.welcome_text);
        setUploadInstructions(data.upload_instructions);
      } catch (error) {
        console.error('Error fetching upload info:', error);
      }
    }
    fetchInfo();
  }, []);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!termsAccepted) {
      setMessage('Please accept the terms and conditions to proceed.');
      setMessageType('error');
      return;
    }

    if (!selectedFile) {
      setMessage('Please select a file to upload.');
      setMessageType('error');
      return;
    }

    setIsUploading(true);
    setMessage('');

    try {
      const data = await uploadFile(selectedFile);
      if (data.success) {
        setMessage(data.message);
        setMessageType('success');
        setSelectedFile(null);
        setTermsAccepted(false);
        const fileInput = document.getElementById('portalFileInput');
        if (fileInput) fileInput.value = '';
      } else {
        setMessage(data.message || 'Upload failed.');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('File upload failed. Please try again.');
      setMessageType('error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <PortalLayout showLogout={true} onNavigate={onNavigate}>
      <div className="cdic-portal-upload-container">

        {/* Welcome Card */}
        <div className="cdic-portal-card-panel">
          <h4 style={{ textAlign: 'center' }}>File Upload</h4>
          <div className="cdic-portal-welcome-text" style={{ textAlign: 'center' }}>
            <p>{welcomeText}</p>
            <p><em>{uploadInstructions}</em></p>
          </div>

          {/* Download Buttons */}
          <div style={{ textAlign: 'center' }}>
            <a href="/api/download/faq" className="cdic-portal-btn-download">
              <i className="material-icons" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                file_download
              </i>
              Download Data Security FAQ
            </a>
            <a href="/api/download/deid-doc" className="cdic-portal-btn-download">
              <i className="material-icons" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                file_download
              </i>
              Download Data De-identification Guide
            </a>
          </div>

          <div style={{ marginTop: '15px' }}>
            <PortalFlashMessage
              message={message}
              type={messageType}
              onClose={() => setMessage('')}
            />
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="cdic-portal-card-panel">
          <h5>Terms and Conditions</h5>
          <div className="cdic-portal-terms-container">
            <h5>1. Introduction</h5>
            <p>By clicking the "Upload Data" button, you agree to comply with the terms and conditions outlined in this agreement. These terms ensure the secure, responsible, and lawful use of data in accordance with international data protection regulations and the applicable Data Use Agreement (DUA).</p>

            <h5>2. Data Use and Compliance</h5>
            <ul>
              <li>The uploaded data must be de-identified to remove direct and indirect patient identifiers in accordance with HIPAA, GDPR, and national data protection regulations.</li>
              <li>Data should only be shared with authorized research personnel as specified in the DUA.</li>
              <li>You confirm that you have the necessary permissions and approvals to upload the data.</li>
            </ul>

            <h5>3. Data Security</h5>
            <ul>
              <li>Data will be stored securely in the Harvard AWS environment, encrypted at rest and in transit.</li>
              <li>Only authorized researchers with valid credentials may access the data.</li>
              <li>Any breach of data security must be reported immediately.</li>
            </ul>

            <h5>4. Prohibited Activities</h5>
            <ul>
              <li>Users must not attempt to re-identify individuals from the uploaded data.</li>
              <li>Data must not be shared, copied, or transferred to unauthorized parties.</li>
              <li>Any attempt to alter or misuse data will result in immediate revocation of access.</li>
            </ul>

            <h5>5. Data Retention and Removal</h5>
            <ul>
              <li>Data will be retained for the duration specified in the DUA (typically five years).</li>
              <li>Upon expiration, data will be archived securely or permanently deleted.</li>
              <li>Users may request data removal in cases of errors or unauthorized uploads.</li>
            </ul>

            <h5>6. Liability and Indemnification</h5>
            <ul>
              <li>You are responsible for ensuring compliance with applicable regulations.</li>
              <li>Harvard University is not liable for unauthorized use or breach by the uploader.</li>
              <li>Users agree to indemnify Harvard University from any claims arising from non-compliance.</li>
            </ul>

            <h5>7. Amendments and Updates</h5>
            <ul>
              <li>These terms may be updated to reflect changes in data protection policies.</li>
              <li>Users will be notified of significant changes.</li>
            </ul>
          </div>
        </div>

        {/* File Upload Form */}
        <div className="cdic-portal-card-panel">
          <form onSubmit={handleUpload}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <input
                  type="file"
                  id="portalFileInput"
                  onChange={handleFileChange}
                  style={{ width: '100%' }}
                />
              </div>
              <button
                type="submit"
                className="cdic-portal-upload-btn"
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
                {!isUploading && (
                  <i className="material-icons" style={{ marginLeft: '5px' }}>cloud_upload</i>
                )}
              </button>
            </div>

            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginRight: '10px' }}
                />
                <span>I have read and agree to the terms and conditions</span>
              </label>
            </div>
          </form>
        </div>
      </div>
    </PortalLayout>
  );
}

export default PortalUploadPage;
